import { useState, useEffect, useCallback } from 'react';
import { alterationsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Scissors, Plus, Trash2, Play, CheckCircle2, Clock, RotateCcw,
  ChevronDown, ChevronUp, Loader2, MapPin, ListChecks,
} from 'lucide-react';
import BodyMapAlteration from './BodyMapAlteration';

const ALTERATION_TYPE_LABELS = {
  shorten: 'Shorten', lengthen: 'Lengthen', take_in: 'Take In',
  let_out: 'Let Out', reshape: 'Reshape', reposition: 'Reposition', other: 'Other',
};

const GARMENT_AREA_LABELS = {
  chest: 'Chest', waist: 'Waist', hips: 'Hips', shoulders: 'Shoulders',
  sleeves: 'Sleeves', jacket_length: 'Jacket Length', trouser_length: 'Trouser Length',
  trouser_waist: 'Trouser Waist', collar: 'Collar', cuffs: 'Cuffs',
  lapels: 'Lapels', lining: 'Lining', other: 'Other',
};

const STATUS_STYLES = {
  recorded: { bg: 'rgba(107,114,128,0.08)', text: '#6B7280', label: 'Recorded' },
  assigned: { bg: 'rgba(37,99,235,0.08)', text: '#2563EB', label: 'Assigned' },
  in_progress: { bg: 'rgba(245,158,11,0.08)', text: '#D97706', label: 'In Progress' },
  completed: { bg: 'rgba(16,185,129,0.08)', text: '#059669', label: 'Completed' },
  qc_pending: { bg: 'rgba(147,51,234,0.08)', text: '#9333EA', label: 'QC Pending' },
  qc_passed: { bg: 'rgba(16,185,129,0.12)', text: '#047857', label: 'QC Passed' },
};

const GARMENT_LABELS = {
  jacket: 'Jacket', trousers: 'Trousers', shirt: 'Shirt', sherwani: 'Sherwani',
  bandhgala: 'Bandhgala', nehru_vest: 'Nehru Vest', kurta: 'Kurta',
  pyjama: 'Pyjama', waistcoat: 'Waistcoat',
};

export default function AlterationsTracker({ orderId, orderItems, orderStatus, onRefresh }) {
  const [alterations, setAlterations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedAlt, setExpandedAlt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [altItems, setAltItems] = useState([]);
  const [altNotes, setAltNotes] = useState('');
  const [timeMatrix, setTimeMatrix] = useState([]);
  const [useBodyMap, setUseBodyMap] = useState(true); // New: Body Map vs Simple mode

  const fetchAlterations = useCallback(async () => {
    try {
      const res = await alterationsAPI.list({ order_id: orderId });
      setAlterations(res.data || []);
    } catch (err) {
      console.error('Failed to fetch alterations', err);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchAlterations();
    alterationsAPI.timeMatrix().then(res => setTimeMatrix(res.data || [])).catch(() => {});
  }, [fetchAlterations]);

  const getEstimatedHours = (type, area, garmentType) => {
    const exact = timeMatrix.find(e => e.alteration_type === type && e.garment_area === area && e.garment_type === garmentType);
    if (exact) return exact.estimated_hours;
    const any = timeMatrix.find(e => e.alteration_type === type && e.garment_area === area && e.garment_type === 'any');
    if (any) return any.estimated_hours;
    const fallback = timeMatrix.find(e => e.alteration_type === 'other' && e.garment_area === 'other');
    return fallback?.estimated_hours || 1.0;
  };

  const openRecordDialog = (item) => {
    setSelectedItem(item);
    setAltItems([{ alteration_type: 'shorten', garment_area: 'sleeves', description: '', adjustment_value: '', estimated_hours: null }]);
    setAltNotes('');
    setShowDialog(true);
  };

  const addAltItem = () => {
    setAltItems(prev => [...prev, { alteration_type: 'other', garment_area: 'other', description: '', adjustment_value: '', estimated_hours: null }]);
  };

  const removeAltItem = (index) => {
    setAltItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateAltItem = (index, field, value) => {
    setAltItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      // Auto-fill estimated hours when type/area changes
      if ((field === 'alteration_type' || field === 'garment_area') && selectedItem) {
        const item = updated[index];
        const hours = getEstimatedHours(item.alteration_type, item.garment_area, selectedItem.garment_type);
        updated[index].estimated_hours = hours;
      }
      return updated;
    });
  };

  const handleSubmitAlteration = async () => {
    if (!altItems.length) { toast.error('Add at least one alteration item'); return; }
    setSubmitting(true);
    try {
      await alterationsAPI.create({
        order_item_id: selectedItem.id,
        items: altItems.map(i => ({
          alteration_type: i.alteration_type,
          garment_area: i.garment_area,
          description: i.description,
          adjustment_value: i.adjustment_value,
          estimated_hours: i.estimated_hours,
        })),
        notes: altNotes || null,
      });
      toast.success('Alteration recorded');
      setShowDialog(false);
      fetchAlterations();
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record alteration');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveNoAlterations = async () => {
    setSubmitting(true);
    try {
      await alterationsAPI.approveNoAlterations({ order_id: orderId });
      toast.success('Approved — ready for delivery');
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusUpdate = async (altId, status) => {
    try {
      await alterationsAPI.updateStatus(altId, { status });
      toast.success(`Alteration status → ${STATUS_STYLES[status]?.label || status}`);
      fetchAlterations();
      onRefresh?.();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const totalAltCost = alterations.reduce((s, a) => s + (a.cost || 0), 0);

  const isTrialPhase = orderStatus === 'ready_for_trial';
  const isAlterationPhase = orderStatus === 'alterations_in_progress';

  return (
    <div className="space-y-4" data-testid="alterations-tracker">
      {/* Action Buttons when in trial phase */}
      {isTrialPhase && (
        <Card className="rounded-xl border-2 bg-white" style={{ borderColor: 'rgba(197,165,90,0.4)' }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>Trial / Alteration Check</p>
                <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                  Record alterations needed after fitting, or approve if no changes required.
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="gap-1.5 rounded-lg text-xs"
                  onClick={handleApproveNoAlterations} disabled={submitting}
                  data-testid="approve-no-alterations-btn">
                  <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#059669' }} />
                  Approve — No Alterations
                </Button>
                {(orderItems || []).map(item => (
                  <Button key={item.id} size="sm" className="gap-1.5 rounded-lg text-xs"
                    style={{ backgroundColor: 'var(--brand-navy)' }}
                    onClick={() => openRecordDialog(item)}
                    data-testid={`record-trial-btn-${item.id}`}>
                    <Scissors className="w-3.5 h-3.5" />
                    Record Trial ({GARMENT_LABELS[item.garment_type] || item.garment_type})
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alteration History */}
      {alterations.length > 0 && (
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
                <RotateCcw className="w-4 h-4" style={{ color: 'var(--brand-gold)' }} />
                Alteration History
                <Badge variant="outline" className="text-[10px] ml-1">{alterations.length}</Badge>
              </CardTitle>
              {totalAltCost > 0 && (
                <span className="text-xs font-semibold tabular-nums" style={{ color: 'var(--ink-muted)' }}>
                  Total Cost: ₹{totalAltCost.toLocaleString('en-IN')}
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {alterations.map(alt => {
              const st = STATUS_STYLES[alt.status] || STATUS_STYLES.recorded;
              const isExpanded = expandedAlt === alt.id;
              return (
                <div key={alt.id} className="rounded-lg border overflow-hidden"
                  style={{ borderColor: st.text + '25' }}
                  data-testid={`alteration-${alt.id}`}>
                  <button onClick={() => setExpandedAlt(isExpanded ? null : alt.id)}
                    className="w-full flex items-center justify-between p-3 hover:bg-gray-50 text-left"
                    style={{ transition: 'background 120ms' }}>
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold"
                        style={{ backgroundColor: st.bg, color: st.text }}>
                        #{alt.trial_number}
                      </div>
                      <div>
                        <p className="text-sm font-semibold" style={{ color: 'var(--navy-ink)' }}>
                          Trial #{alt.trial_number}
                          <span className="text-[10px] font-normal capitalize ml-1.5" style={{ color: 'var(--ink-muted)' }}>
                            ({GARMENT_LABELS[alt.garment_type] || alt.garment_type})
                          </span>
                        </p>
                        <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                          {alt.items?.length || 0} item(s) · ~{alt.estimated_hours}h
                          {alt.assigned_worker_name && <> · {alt.assigned_worker_name}</>}
                          {alt.cost > 0 && <> · ₹{alt.cost.toLocaleString('en-IN')}</>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="text-[10px] px-2 py-0.5 font-bold"
                        style={{ backgroundColor: st.bg, color: st.text }}>
                        {st.label}
                      </Badge>
                      {isExpanded ? <ChevronUp className="w-3.5 h-3.5" style={{ color: 'var(--ink-muted)' }} /> : <ChevronDown className="w-3.5 h-3.5" style={{ color: 'var(--ink-muted)' }} />}
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="px-3 pb-3 border-t" style={{ borderColor: 'rgba(27,42,74,0.06)' }}>
                      <div className="space-y-1 mt-2">
                        {(alt.items || []).map((item, i) => (
                          <div key={item.id || i} className="flex items-center gap-2 py-1.5 px-2 rounded"
                            style={{ backgroundColor: 'rgba(27,42,74,0.02)' }}>
                            <span className="text-xs font-semibold w-20" style={{ color: 'var(--navy-ink)' }}>
                              {ALTERATION_TYPE_LABELS[item.alteration_type] || item.alteration_type}
                            </span>
                            <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                              {GARMENT_AREA_LABELS[item.garment_area] || item.garment_area}
                            </span>
                            {item.adjustment_value && (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 font-mono">{item.adjustment_value}</Badge>
                            )}
                            <span className="text-[9px] ml-auto tabular-nums" style={{ color: 'var(--ink-muted)' }}>
                              ~{item.estimated_hours}h
                            </span>
                          </div>
                        ))}
                      </div>
                      {alt.notes && (
                        <p className="text-[10px] mt-2 italic px-1 py-1 rounded"
                          style={{ backgroundColor: 'rgba(27,42,74,0.03)', color: 'var(--ink-muted)' }}>
                          Notes: {alt.notes}
                        </p>
                      )}
                      {/* Status actions */}
                      <div className="flex gap-2 mt-3">
                        {alt.status === 'assigned' && (
                          <Button size="sm" variant="outline" className="gap-1 text-[10px] h-7 rounded-md"
                            onClick={() => handleStatusUpdate(alt.id, 'in_progress')}
                            data-testid={`start-alt-${alt.id}`}>
                            <Play className="w-3 h-3" /> Start
                          </Button>
                        )}
                        {alt.status === 'in_progress' && (
                          <Button size="sm" variant="outline" className="gap-1 text-[10px] h-7 rounded-md"
                            onClick={() => handleStatusUpdate(alt.id, 'completed')}
                            data-testid={`complete-alt-${alt.id}`}>
                            <CheckCircle2 className="w-3 h-3" /> Mark Complete
                          </Button>
                        )}
                        {alt.status === 'recorded' && (
                          <Button size="sm" variant="outline" className="gap-1 text-[10px] h-7 rounded-md"
                            onClick={() => handleStatusUpdate(alt.id, 'in_progress')}
                            data-testid={`start-alt-${alt.id}`}>
                            <Play className="w-3 h-3" /> Start Work
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {!isTrialPhase && !isAlterationPhase && alterations.length === 0 && (
        <p className="text-xs text-center py-4" style={{ color: 'var(--ink-muted)' }}>
          No alterations recorded. Alterations can be recorded when the order is in "Ready for Trial" status.
        </p>
      )}

      {/* Record Alteration Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className={`max-h-[90vh] overflow-y-auto ${useBodyMap ? 'max-w-3xl' : 'max-w-lg'}`} data-testid="record-alteration-dialog">
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--brand-navy)' }}>
              Record Trial — {selectedItem && (GARMENT_LABELS[selectedItem.garment_type] || selectedItem.garment_type)}
            </DialogTitle>
            <div className="flex items-center gap-3 mt-2">
              <div className="flex items-center gap-2">
                <Switch
                  checked={useBodyMap}
                  onCheckedChange={setUseBodyMap}
                  id="body-map-toggle"
                  data-testid="body-map-toggle"
                />
                <label htmlFor="body-map-toggle" className="text-xs cursor-pointer flex items-center gap-1.5" style={{ color: 'var(--ink-muted)' }}>
                  {useBodyMap ? <MapPin className="w-3 h-3" /> : <ListChecks className="w-3 h-3" />}
                  {useBodyMap ? 'Body Map View' : 'Simple View'}
                </label>
              </div>
            </div>
          </DialogHeader>
          
          {useBodyMap ? (
            /* Body Map Interface */
            <BodyMapAlteration
              garmentType={selectedItem?.garment_type || 'jacket'}
              garmentLabel={selectedItem && (GARMENT_LABELS[selectedItem.garment_type] || selectedItem.garment_type)}
              existingAlterations={[]}
              onSave={async (alterations) => {
                // Convert body map alterations to the format expected by the API
                const items = alterations.map(alt => ({
                  alteration_type: alt.alterationType,
                  garment_area: alt.zoneId,
                  description: alt.notes || '',
                  adjustment_value: alt.adjustment ? `${alt.adjustment > 0 ? '+' : ''}${alt.adjustment}cm` : '',
                  estimated_hours: alt.estimatedHours,
                }));
                
                setSubmitting(true);
                try {
                  await alterationsAPI.create({
                    order_item_id: selectedItem.id,
                    items,
                    notes: altNotes || null,
                  });
                  toast.success('Alteration recorded');
                  setShowDialog(false);
                  fetchAlterations();
                  onRefresh?.();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to record alteration');
                } finally {
                  setSubmitting(false);
                }
              }}
              onCancel={() => setShowDialog(false)}
            />
          ) : (
            /* Simple Text-Based Interface */
            <>
              <div className="space-y-4">
                {altItems.map((item, i) => {
                  const totalHours = altItems.reduce((s, a) => s + (a.estimated_hours || 0), 0);
                  return (
                    <div key={i} className="rounded-lg border p-3 space-y-2" style={{ borderColor: 'rgba(27,42,74,0.1)' }}
                      data-testid={`alt-item-row-${i}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold" style={{ color: 'var(--navy-ink)' }}>Item {i + 1}</span>
                        {altItems.length > 1 && (
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => removeAltItem(i)}>
                            <Trash2 className="w-3 h-3" style={{ color: '#DC2626' }} />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px]">Type</Label>
                          <Select value={item.alteration_type} onValueChange={v => updateAltItem(i, 'alteration_type', v)}>
                            <SelectTrigger className="h-8 mt-0.5 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {Object.entries(ALTERATION_TYPE_LABELS).map(([k, v]) => (
                                <SelectItem key={k} value={k}>{v}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[10px]">Area</Label>
                          <Select value={item.garment_area} onValueChange={v => updateAltItem(i, 'garment_area', v)}>
                            <SelectTrigger className="h-8 mt-0.5 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {Object.entries(GARMENT_AREA_LABELS).map(([k, v]) => (
                                <SelectItem key={k} value={k}>{v}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px]">Adjustment</Label>
                          <Input value={item.adjustment_value} placeholder="e.g. -1cm"
                            onChange={e => updateAltItem(i, 'adjustment_value', e.target.value)}
                            className="h-8 mt-0.5 text-xs" />
                        </div>
                        <div>
                          <Label className="text-[10px]">Est. Hours</Label>
                          <Input type="number" step="0.25" value={item.estimated_hours ?? ''}
                            onChange={e => updateAltItem(i, 'estimated_hours', parseFloat(e.target.value) || null)}
                            className="h-8 mt-0.5 text-xs" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-[10px]">Description</Label>
                        <Input value={item.description} placeholder="Optional description"
                          onChange={e => updateAltItem(i, 'description', e.target.value)}
                          className="h-8 mt-0.5 text-xs" />
                      </div>
                    </div>
                  );
                })}

                <Button variant="outline" size="sm" className="gap-1 text-xs w-full" onClick={addAltItem}
                  data-testid="add-alt-item-btn">
                  <Plus className="w-3 h-3" /> Add Alteration Item
                </Button>

                <div className="flex items-center justify-between px-2 py-1.5 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.04)' }}>
                  <span className="text-xs font-medium" style={{ color: 'var(--navy-ink)' }}>Total Estimated</span>
                  <span className="text-sm font-bold tabular-nums" style={{ color: 'var(--brand-navy)' }}>
                    {altItems.reduce((s, a) => s + (a.estimated_hours || 0), 0).toFixed(1)}h
                  </span>
                </div>

                <div>
                  <Label className="text-xs">Notes</Label>
                  <Textarea value={altNotes} onChange={e => setAltNotes(e.target.value)}
                    placeholder="Trial feedback, general notes..." rows={2} className="mt-1" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" size="sm" onClick={() => setShowDialog(false)}>Cancel</Button>
                <Button size="sm" onClick={handleSubmitAlteration} disabled={submitting || altItems.length === 0}
                  className="gap-1" style={{ backgroundColor: 'var(--brand-navy)' }}
                  data-testid="submit-alteration-btn">
                  {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Scissors className="w-3 h-3" />}
                  Save Alteration
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
